import { useAuth } from 'react-oidc-context';
import { useAnalyticsSession } from '@consolidocs-ui/analytics';
import {
  useGetPermissionsQuery,
  useGetUserByIdQuery,
} from '@consolidocs-ui/data-access/UserApi';
import useUserPermissions from '@consolidocs-ui/hooks/useUserPermissions';

const fullName = (...parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

/**
 * Tells analytics who is signed in and in which role, so every event can be
 * broken down by user, organisation, department and role. Both queries are
 * cache reads: the profile is already loaded for the language preference and
 * the permissions for routing.
 */
export const useAnalyticsUser = (): void => {
  const { user } = useAuth();
  const userId = user?.profile.sub;

  const { profile } = useGetUserByIdQuery(userId ?? '', {
    skip: !userId,
    selectFromResult: ({ data }) => ({ profile: data?.data }),
  });
  const { organizationId } = useGetPermissionsQuery('', {
    selectFromResult: ({ data }) => ({ organizationId: data?.organization?.id }),
  });
  const { roles, selectedRoleKey, roleFile, departmentId } =
    useUserPermissions();
  const selectedRole = roles?.find(
    (role) => `${role.role_id}/${role.department_id}` === selectedRoleKey
  );

  // The same fields the Profile page shows and edits; first_name/last_name are
  // typed on UserType but this response does not fill them.
  // Without a name Mixpanel labels the profile with its id (the Cognito sub),
  // so fall back to the names on the ID token.
  const name =
    fullName(profile?.given_name, profile?.family_name) ||
    fullName(user?.profile.given_name, user?.profile.family_name);

  useAnalyticsSession(
    userId
      ? {
          id: userId,
          email: profile?.email ?? user?.profile.email,
          name: name || user?.profile.name,
          properties: {
            role: roleFile || undefined,
            role_name: selectedRole?.role,
            department: selectedRole?.department,
            department_id: departmentId,
            job_role: selectedRole?.job_role_data?.name,
            organization_id: organizationId,
          },
        }
      : null
  );
};
